import React from "react"
import classNames from "classnames"
import { connect } from "react-redux"
import { useHistory, Redirect } from "react-router-dom"
import { toast } from "react-toastify"

import styles from "./MfaVerifyContainer.module.scss"
import MfaVerifyForm from "../../forms/MfaVerifyForm/MfaVerifyForm"
import services from "../../services"
import { userLogin } from "../../actions/user/userActions"
import { save } from "../../helpers/localStorage"
import { selectFormattedUser } from "../../reducers/selectors"
import { paths } from "../../routes/paths"
import ToastInfo from "../../components/ToastContents/ToastInfo"
import {
  isRateLimitError,
  getMfaRateLimitMessage,
  getRetryAfterSeconds,
  showMfaRateLimitToast,
  getMfaErrorMessage,
  isMfaSessionExpiredError,
  isMfaExhaustedError,
} from "../../helpers/mfaErrors"

const MfaVerifyContainer = ({ userLogin }) => {
  const history = useHistory()
  const [resendMessage, setResendMessage] = React.useState("")
  const [resendDisabled, setResendDisabled] = React.useState(false)
  const [smsResendDisabled, setSmsResendDisabled] = React.useState(false)
  const [smsSending, setSmsSending] = React.useState(false)
  const [sessionExpired, setSessionExpired] = React.useState(false)
  const [statusMessage, setStatusMessage] = React.useState("")
  const [verificationMode, setVerificationMode] = React.useState("email")
  const [otpChannel, setOtpChannel] = React.useState("email")
  const cooldownTimerRef = React.useRef(null)
  const smsCooldownTimerRef = React.useRef(null)
  const abortedRef = React.useRef(false)
  const verifyRequestIdRef = React.useRef(0)
  const mfaToken = sessionStorage.getItem("mfaToken")
  const smsBackupAvailable =
    sessionStorage.getItem("mfaSmsBackupAvailable") === "true"

  React.useEffect(
    () => () => {
      abortedRef.current = true
      verifyRequestIdRef.current += 1
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current)
      if (smsCooldownTimerRef.current) clearInterval(smsCooldownTimerRef.current)
    },
    [],
  )

  if (!mfaToken) {
    return <Redirect to={paths.login} />
  }

  const goToLogin = () => {
    abortedRef.current = true
    verifyRequestIdRef.current += 1
    sessionStorage.removeItem("mfaToken")
    sessionStorage.removeItem("mfaSmsBackupAvailable")
    history.push(paths.login)
  }

  const handleVerify = (values) =>
    new Promise((resolve, reject) => {
      if (sessionExpired || abortedRef.current) {
        reject(new Error("session expired"))
        return
      }
      const requestId = ++verifyRequestIdRef.current
      const currentToken = sessionStorage.getItem("mfaToken")
      const payload = {
        mfa_token: currentToken,
        remember_device: values.remember_device,
        browser: navigator.userAgent,
        os: navigator.platform,
        device_name: navigator.userAgent,
      }
      if (values.recovery_code) {
        payload.recovery_code = String(values.recovery_code).replace(/\D/g, "")
      } else {
        payload.otp = values.otp
      }

      services.userServices
        .verifyMfa(payload)
        .then((res) => {
          if (abortedRef.current || requestId !== verifyRequestIdRef.current) {
            resolve()
            return
          }
          sessionStorage.removeItem("mfaToken")
          sessionStorage.removeItem("mfaSmsBackupAvailable")
          userLogin(res.data)
          save("currentUser", selectFormattedUser(res.data))
          history.push(paths.home)
          resolve()
        })
        .catch((error) => {
          if (abortedRef.current || requestId !== verifyRequestIdRef.current) {
            resolve()
            return
          }
          if (isRateLimitError(error)) {
            showMfaRateLimitToast(error)
          } else if (isMfaSessionExpiredError(error)) {
            setSessionExpired(true)
            setStatusMessage(getMfaErrorMessage(error))
            toast.error(<ToastInfo>{getMfaErrorMessage(error)}</ToastInfo>, {
              position: toast.POSITION.TOP_RIGHT,
            })
          } else if (isMfaExhaustedError(error)) {
            setStatusMessage(getMfaErrorMessage(error))
            toast.error(<ToastInfo>{getMfaErrorMessage(error)}</ToastInfo>, {
              position: toast.POSITION.TOP_RIGHT,
            })
          }
          reject(error)
        })
    })

  const startResendCooldownMessage = (error) => {
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current)

    let remaining = getRetryAfterSeconds(error)
    setResendMessage(getMfaRateLimitMessage(error, remaining || null))
    showMfaRateLimitToast(error)

    if (remaining <= 1) {
      setTimeout(() => setResendDisabled(false), 3000)
      return
    }

    cooldownTimerRef.current = setInterval(() => {
      remaining -= 1
      if (remaining <= 0) {
        clearInterval(cooldownTimerRef.current)
        cooldownTimerRef.current = null
        setResendDisabled(false)
        return
      }
      setResendMessage(getMfaRateLimitMessage(error, remaining))
    }, 1000)
  }

  const startSmsResendCooldownMessage = (error) => {
    if (smsCooldownTimerRef.current) clearInterval(smsCooldownTimerRef.current)

    let remaining = getRetryAfterSeconds(error)
    setResendMessage(getMfaRateLimitMessage(error, remaining || null))
    showMfaRateLimitToast(error)

    if (remaining <= 1) {
      setTimeout(() => setSmsResendDisabled(false), 3000)
      return
    }

    smsCooldownTimerRef.current = setInterval(() => {
      remaining -= 1
      if (remaining <= 0) {
        clearInterval(smsCooldownTimerRef.current)
        smsCooldownTimerRef.current = null
        setSmsResendDisabled(false)
        return
      }
      setResendMessage(getMfaRateLimitMessage(error, remaining))
    }, 1000)
  }

  const handleResend = () => {
    const currentToken = sessionStorage.getItem("mfaToken")
    if (!currentToken || resendDisabled || sessionExpired) return

    setResendDisabled(true)
    services.userServices
      .resendMfa(currentToken)
      .then((res) => {
        if (res.data?.mfa_token) {
          sessionStorage.setItem("mfaToken", res.data.mfa_token)
        }
        setStatusMessage("")
        setOtpChannel("email")
        setResendMessage("A new code has been sent to your email.")
        toast.success(
          <ToastInfo>A new code has been sent to your email.</ToastInfo>,
          { position: toast.POSITION.TOP_RIGHT },
        )
        setTimeout(() => setResendDisabled(false), 3000)
      })
      .catch((error) => {
        if (isMfaSessionExpiredError(error)) {
          setSessionExpired(true)
          const message = getMfaErrorMessage(error)
          setStatusMessage(message)
          setResendMessage(message)
          toast.error(<ToastInfo>{message}</ToastInfo>, {
            position: toast.POSITION.TOP_RIGHT,
          })
          setTimeout(() => setResendDisabled(false), 3000)
          return
        }

        if (isRateLimitError(error)) {
          startResendCooldownMessage(error)
          return
        }

        const message =
          getMfaErrorMessage(error) ||
          "Unable to resend code. Please try again later."
        setResendMessage(message)
        toast.error(<ToastInfo>{message}</ToastInfo>, {
          position: toast.POSITION.TOP_RIGHT,
        })
        setTimeout(() => setResendDisabled(false), 3000)
      })
  }

  const handleResendSms = () => {
    const currentToken = sessionStorage.getItem("mfaToken")
    if (!currentToken || smsResendDisabled || smsSending || sessionExpired) return

    setSmsSending(true)
    setSmsResendDisabled(true)
    setStatusMessage("")
    setResendMessage("Sending a code to your mobile number...")
    services.userServices
      .resendMfaSms(currentToken)
      .then((res) => {
        if (res.data?.mfa_token) {
          sessionStorage.setItem("mfaToken", res.data.mfa_token)
        }
        setStatusMessage("")
        setOtpChannel("sms")
        setResendMessage("Code sent. Check your phone for the text message.")
        toast.success(
          <ToastInfo>Code sent. Check your phone for the text message.</ToastInfo>,
          { position: toast.POSITION.TOP_RIGHT },
        )
        setTimeout(() => setSmsResendDisabled(false), 3000)
      })
      .catch((error) => {
        if (isMfaSessionExpiredError(error)) {
          setSessionExpired(true)
          const message = getMfaErrorMessage(error)
          setStatusMessage(message)
          setResendMessage(message)
          toast.error(<ToastInfo>{message}</ToastInfo>, {
            position: toast.POSITION.TOP_RIGHT,
          })
          setTimeout(() => setSmsResendDisabled(false), 3000)
          return
        }

        if (isRateLimitError(error)) {
          startSmsResendCooldownMessage(error)
          return
        }

        const message =
          getMfaErrorMessage(error) ||
          "Unable to send a text code. Please try again later."
        setResendMessage(message)
        toast.error(<ToastInfo>{message}</ToastInfo>, {
          position: toast.POSITION.TOP_RIGHT,
        })
        setTimeout(() => setSmsResendDisabled(false), 3000)
      })
      .finally(() => setSmsSending(false))
  }

  return (
    <div className={styles.signInWrap}>
      <div
        className={classNames("smallContainer", "fullWidth", styles.inlineWrap)}
      >
        <MfaVerifyForm
          key={`${verificationMode}-${otpChannel}`}
          handleVerify={handleVerify}
          onResend={handleResend}
          onResendSms={smsBackupAvailable ? handleResendSms : undefined}
          resendDisabled={resendDisabled || sessionExpired}
          smsResendDisabled={smsResendDisabled || smsSending || sessionExpired}
          smsSending={smsSending}
          smsBackupAvailable={smsBackupAvailable}
          otpChannel={otpChannel}
          resendMessage={resendMessage}
          sessionExpired={sessionExpired}
          statusMessage={statusMessage}
          onReturnToLogin={goToLogin}
          verificationMode={verificationMode}
          onSwitchMode={setVerificationMode}
        />
      </div>
    </div>
  )
}

export default connect(null, {
  userLogin,
})(MfaVerifyContainer)
